import { FormEventHandler, ReactNode } from "react";
import { Button } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import useIsSmall from "@/hooks/useIsSmall";
import classes from "./settings.module.css";

export const SETTINGS_ROOT = "/dashboard/profile/edit";

// The salon settings, in two groups (approved canvas «تنظیمات مجموعه»).
export const settingsGroups = [
  {
    title: "اطلاعات",
    items: [
      { to: `${SETTINGS_ROOT}/merchantProfile`, title: "پروفایل سالن", hint: "لوگو، نام، نوع و معرفی" },
      { to: `${SETTINGS_ROOT}/socialMedia`, title: "تماس و آدرس", hint: "تلفن، شبکه‌ها، آدرس و نقشه" },
      { to: `${SETTINGS_ROOT}/workHours`, title: "ساعت کاری", hint: "روزها و ساعت‌های باز بودن" },
    ],
  },
  {
    title: "سایت رزرو",
    items: [
      { to: `${SETTINGS_ROOT}/seo`, title: "سایت و سئو", hint: "نمایش در گوگل و اشتراک‌گذاری" },
      { to: `${SETTINGS_ROOT}/gallery`, title: "گالری تصاویر", hint: "عکس‌ها و توضیح هر کدام" },
      { to: `${SETTINGS_ROOT}/siteTemplate`, title: "قالب و رنگ سایت", hint: "ظاهر سایت رزرو" },
      { to: `${SETTINGS_ROOT}/siteQrCode`, title: "کد QR", hint: "برای چاپ روی کارت و میز" },
    ],
  },
];

type Props = {
  title: string;
  description?: string;
  children: ReactNode;
  // A form page: its save/cancel. Without onSubmit the shell is a plain page.
  onSubmit?: FormEventHandler<HTMLFormElement>;
  dirty?: boolean;
  saving?: boolean;
  onReset?: () => void;
};

// Settings page frame: side menu and a slim «تغییرات ذخیره نشده» bar on desktop; on phones a title bar with
// back and «ذخیره». Content is a column of SettingsSection blocks.
export default function SettingsShell({ title, description, children, onSubmit, dirty = false, saving = false, onReset }: Props) {
  const isSmall = useIsSmall();
  const { pathname } = useLocation();
  // The settings hub used to link back to itself, making its back button appear broken.
  // Child settings return to the hub; the hub returns to the profile page.
  const backRoute = pathname === SETTINGS_ROOT ? "/dashboard" : SETTINGS_ROOT;
  // Always a form, so Enter in a field submits on form pages; plain pages ignore it.
  const submit: FormEventHandler<HTMLFormElement> = onSubmit ?? ((event) => event.preventDefault());

  if (isSmall)
    return (
      <form className={classes.mobile} dir="rtl" onSubmit={submit}>
        <header className={classes.mobileBar}>
          <Link to={backRoute} className={classes.back} aria-label="بازگشت"><IconArrowRight size={22} /></Link>
          <strong>{title}</strong>
          {onSubmit && <Button type="submit" color="violet" size="sm" disabled={!dirty} loading={saving}>ذخیره</Button>}
        </header>
        <div className={classes.mobileBody}>
          {description && <p className={classes.lead}>{description}</p>}
          {children}
        </div>
      </form>
    );

  return (
    <form className={classes.desktop} dir="rtl" onSubmit={submit}>
      {onSubmit && dirty && (
        <div className={classes.dirtyBar}>
          <span><i />تغییرات ذخیره نشده</span>
          <span className={classes.dirtyActions}>
            <Button variant="default" onClick={onReset} disabled={saving}>انصراف</Button>
            <Button type="submit" color="violet" loading={saving}>ذخیره</Button>
          </span>
        </div>
      )}
      <div className={classes.layout}>
        <nav className={classes.nav} aria-label="بخش‌های تنظیمات">
          <span className={classes.navTitle}>تنظیمات مجموعه</span>
          {settingsGroups.flatMap((group) => group.items).map((item) => (
            <Link key={item.to} to={item.to} className={classes.navItem} aria-current={pathname === item.to ? "page" : undefined}>{item.title}</Link>
          ))}
        </nav>
        <main className={classes.main}>
          <header className={classes.head}>
            <h1>{title}</h1>
            {description && <p>{description}</p>}
          </header>
          {children}
        </main>
      </div>
    </form>
  );
}

// One settings block: what it is and why on one side, its fields in a card on the other.
export function SettingsSection({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className={classes.section}>
      <div className={classes.sectionInfo}>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      <div className={classes.sectionCard}>{children}</div>
    </section>
  );
}
